import React, { FC, useEffect, useState } from "react"
import { RootState } from "../../../store"
import { connect } from "react-redux"
import { LCReleaseForm, RoyaltiesProps, User } from "../../../types"
import Reports from "./reports/Reports"
import { KButton, KSpan, KTitleSpan, KTooltip, KDropdown } from "kahuna-base-react-components"
import { lang } from "../../../constants/languages"
import Balance from "./balance/Balance"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { handleAuthorizedRequest, summaries, users } from "../../../apis"
import { chartLabels, platformIcons, USER_MEMBER_TYPE } from "../../../constants"
import Upcoming from "./upcoming/Upcoming"
import Statements from "./statements/Statements"
import SearchUserComponent from "../../ui/components/SearchUserComponent"

enum TAB_OPTIONS {
  BALANCE = "balance",
  UPCOMING = "upcoming",
  STATEMENTS = "statements",
  REPORTS = "reports"
}

const userAdvanceMemberOptions: KSelectOption[] = [
  { label: lang.royalties.user_members.main_account, value: 1, value2: "main" },
  { label: lang.royalties.user_members.advance_account, value: 2, value2: "advance" },
  { label: lang.royalties.user_members.all_accounts, value: 3, value2: "all" }
]

const userMemberOptions: KSelectOption[] = [
  { label: lang.royalties.user_members.main_account, value: 1, value2: "main" },
  { label: lang.royalties.user_members.all_accounts, value: 3, value2: "all" }
]

const Royalties: FC<RoyaltiesProps> = (props) => {
  const [selectedTab, setSelectedTab] = useState(TAB_OPTIONS.BALANCE)
  const [platforms, setPlatforms] = useState<KSelectOption[]>([])
  const [openSidebar, setOpenSidebar] = useState<boolean>(true)
  const [selectedUser, setSelectedUser] = useState<KSelectOption>()
  const [selectedAccount, setSelectedAccount] = useState<KSelectOption>({
    label: lang.royalties.user_members.main_account,
    value: 1,
    value2: "main"
  })

  useEffect(() => {
    summaries(`/dsp_options/`, "get")
      .then((resp) => {
        if (resp.data.results) {
          setPlatforms(
            resp.data.results.map((option: string) => {
              //@ts-ignore
              return { label: chartLabels.platform[option], value: option, icon: platformIcons[option] }
            })
          )
        }
      })
      .catch(() => {})
  }, [])

  const renderTabs = () => {
    return (
      <div className="flex gap-4 ml-3">
        {Object.values(TAB_OPTIONS).map((option) => {
          return (
            <div
              className="cursor-pointer"
              onClick={() => setSelectedTab(option)}
              style={{ borderBottom: selectedTab === option ? "1px solid #666666" : "" }}
            >
              <KSpan
                text={lang.royalties[option].title}
                fontWeight={selectedTab === option ? 500 : 400}
                color={selectedTab === option ? "#111111" : ""}
              />
            </div>
          )
        })}
      </div>
    )
  }

  const renderUserMembers = () => {
    const userMembers = props.user.userMembers || []
    if (userMembers.length === 0) {
      return <div />
    }

    const options =
      userMembers.filter((userMember) => userMember.type === USER_MEMBER_TYPE.ADVANCE).length === 0
        ? userMemberOptions
        : userAdvanceMemberOptions

    return (
      <div className="pr-3">
        <KDropdown
          options={options}
          isClearable={false}
          width={180}
          menuWidth={180}
          selected={selectedAccount}
          onSelect={(selected) => setSelectedAccount(selected as KSelectOption)}
        />
      </div>
    )
  }

  return (
    <div
      className="catalog flex bg-[#fff]"
      style={{
        flexDirection: props.isAdmin ? "row" : "column"
      }}
    >
      {props.isAdmin && (
        <div
          className={`${
            openSidebar ? "min-w-[290px]" : "min-w-0 !w-[0px]"
          } max-w-[320px] w-[20%] bg-[#F7F7F7] min-h-[100vh] flex flex-col overflow-hidden p-5 pl-6 pt-4`}
          style={{ transitionDuration: "0.5s" }}
        >
          <KTitleSpan text="Royalties" fontSize={20} lineHeight="24px" />
          <div className="mt-6">
            <SearchUserComponent className="px-0 mb-6" selectedUser={selectedUser} setSelectedUser={setSelectedUser} />
          </div>
        </div>
      )}
      <div style={{ width: "100%", padding: props.isAdmin ? 24 : 0 }}>
        {props.isAdmin && (
          <KTooltip
            backgroundColor="#000"
            padding="0px 4px"
            height="20px"
            arrowColor="#000"
            position="bottom"
            content={
              <div className="h-5 w-max flex items-center">
                <KSpan
                  text={openSidebar ? "Hide sidebar" : "Open sidebar"}
                  fontSize={12}
                  lineHeight="16px"
                  color="#FFF"
                />
              </div>
            }
          >
            <div className="flex items-center mb-4 gap-2">
              <KButton
                icon="/left-sidebar.svg"
                width="32px"
                height="32px"
                background="transparent"
                shadowDisabled={true}
                padding="6px"
                onClick={() => {
                  if (setOpenSidebar) {
                    setOpenSidebar(!openSidebar)
                  }
                }}
              />
              <KTitleSpan text="Royalties" fontSize={20} lineHeight="24px" />
            </div>
          </KTooltip>
        )}
        <div className="flex justify-between">
          {renderTabs()}
          {!props.isAdmin && renderUserMembers()}
        </div>
        {selectedTab === TAB_OPTIONS.BALANCE && (
          <Balance
            isAdmin={props.isAdmin}
            platforms={platforms}
            selectedUser={selectedUser}
            selectedAccount={selectedAccount}
          />
        )}
        {selectedTab === TAB_OPTIONS.UPCOMING && (
          <Upcoming
            isAdmin={props.isAdmin}
            platforms={platforms}
            selectedUser={selectedUser}
            selectedAccount={selectedAccount}
          />
        )}
        {selectedTab === TAB_OPTIONS.STATEMENTS && (
          <Statements isAdmin={props.isAdmin} selectedUser={selectedUser} selectedAccount={selectedAccount} />
        )}
        {selectedTab === TAB_OPTIONS.REPORTS && (
          <Reports isAdmin={props.isAdmin} selectedUser={selectedUser} selectedAccount={selectedAccount} />
        )}
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    user: state.auth.user
  }
}

export default connect(mapStateToProps, {})(Royalties)
